import { InputRow, SelectRow, DetailRow, SwitchRow } from "../Rows";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import EntityMultiSelectForm from "../EntityMultiSelectForm";
import { FormCard, FormGrid, FormSection } from "../FormCard";

/** Maps API connector types (string legacy or { typeName, description }) to SelectRow options: value = stored code, label = description for display. */
const mapConnectorStandardsToOptions = (standards) => {
    if (!Array.isArray(standards)) {
        return [];
    }

    return standards
        .map((s) => {
            if (typeof s === "string") {
                const v = s.trim();
                if (!v) {
                    return null;
                }

                return { value: v, label: v };
            }

            const value = (s.typeName ?? s.TypeName ?? "").trim();
            if (!value) {
                return null;
            }

            const description = (s.description ?? s.Description ?? "").trim();
            return {
                value,
                label: description.length > 0 ? description : value,
            };
        })
        .filter(Boolean);
};

const ConnectorForm = ({
    chargingConnector,
    handleInputChange,
    chargingPoints = [],
    invalidFields = [],
    isEdit,
    standards = [],
    tariffs = []
}) => {
    const { t } = useTranslation();
    const standardOptions = mapConnectorStandardsToOptions(standards);

    const selectedTariffIds = useMemo(
        () => chargingConnector?.tariffIds || [],
        [chargingConnector?.tariffIds]
    );

    const formatOptions = [
        { value: "SOCKET", label: t("connector.format.SOCKET") },
        { value: "CABLE", label: t("connector.format.CABLE") }
    ];

    const pointOptions = chargingPoints.map((p) => ({
        value: p.chargingPointPublicId,
        label: `${p.chargingPointName} (${p.chargingSiteName})`
    }));

    const selectedPointLabel = (() => {
        const p = chargingPoints.find(
            (x) => x.chargingPointPublicId === chargingConnector?.chargingPointPublicId
        );
        return p ? `${p.chargingPointName} (${p.chargingSiteName})` : "";
    })();

    const parentPoint = useMemo(() => {
        return chargingPoints.find(
            (x) => x.chargingPointPublicId === chargingConnector?.chargingPointPublicId
        );
    }, [chargingPoints, chargingConnector?.chargingPointPublicId]);

    const powerTypeOptions = [
        { value: "AC_1_PHASE", label: t("connector.powerType.AC_1_PHASE") },
        { value: "AC_2_PHASE", label: t("connector.powerType.AC_2_PHASE") },
        { value: "AC_2_PHASE_SPLIT", label: t("connector.powerType.AC_2_PHASE_SPLIT") },
        { value: "AC_3_PHASE", label: t("connector.powerType.AC_3_PHASE") },
        { value: "DC", label: t("connector.powerType.DC") }
    ];

    const capabilityOptions = [
        {
            value: "ISO_15118_2_PLUG_AND_CHARGE",
            label: t("connector.capability.ISO_15118_2_PLUG_AND_CHARGE")
        },
        {
            value: "ISO_15118_20_PLUG_AND_CHARGE",
            label: t("connector.capability.ISO_15118_20_PLUG_AND_CHARGE")
        }
    ];

    return (
        <div className="w-full">
            <FormCard title={t("form.connectorDetails")}>
                <FormGrid>
                    <InputRow
                        label={t("common.name")}
                        value={chargingConnector?.chargingConnectorName}
                        onChange={(v) => handleInputChange("chargingConnectorName", v)}
                        invalid={invalidFields.includes("chargingConnectorName")}
                        required
                    />

                    {isEdit ? (
                        <DetailRow label={t("entity.point")} value={selectedPointLabel} />
                    ) : (
                        <SelectRow
                            label={t("entity.point")}
                            value={chargingConnector?.chargingPointPublicId || ""}
                            options={pointOptions}
                            onChange={(v) => handleInputChange("chargingPointPublicId", v)}
                            required
                            invalid={invalidFields.includes("chargingPointPublicId")}
                        />
                    )}

                    <InputRow
                        label={t("form.connectorNumber")}
                        value={chargingConnector?.chargingConnectorNumber}
                        onChange={(v) => handleInputChange("chargingConnectorNumber", v)}
                        isNumber
                        invalid={invalidFields.includes("chargingConnectorNumber")}
                        required
                    />

                    <SelectRow
                        label={t("form.standard")}
                        tooltip={t("connector.tooltip.standard")}
                        value={chargingConnector?.standard}
                        options={standardOptions}
                        onChange={(v) => handleInputChange("standard", v)}
                        invalid={invalidFields.includes("standard")}
                        required
                    />

                    <SelectRow
                        label={t("form.format")}
                        tooltip={t("connector.tooltip.format")}
                        value={chargingConnector?.format}
                        options={formatOptions}
                        onChange={(v) => handleInputChange("format", v)}
                        invalid={invalidFields.includes("format")}
                        required
                    />

                    <SelectRow
                        label={t("form.capabilities")}
                        tooltip={t("connector.tooltip.capabilities")}
                        value={chargingConnector?.capabilities || []}
                        options={capabilityOptions}
                        onChange={(v) => handleInputChange("capabilities", v || [])}
                        isMulti
                    />

                    <InputRow
                        label={t("form.termsUrl")}
                        tooltip={t("connector.tooltip.termsAndConditions")}
                        value={chargingConnector?.termsAndConditions}
                        onChange={(v) => handleInputChange("termsAndConditions", v)}
                    />

                    {isEdit && (
                        <>
                            {parentPoint?.supportsReservation ? (
                                <SwitchRow
                                    label={t("form.reservationEnabled")}
                                    tooltip={t("connector.tooltip.reservationEnabled")}
                                    checked={!!chargingConnector?.reservationEnabled}
                                    onChange={(v) => handleInputChange("reservationEnabled", v)}
                                />
                            ) : (
                                <DetailRow
                                    label={t("form.reservationEnabled")}
                                    value={t("connector.pointNoReservation")}
                                />
                            )}
                        </>
                    )}

                    <FormSection title={t("form.powerSettings")} className="col-span-1 md:col-span-2 mt-10">
                        <FormGrid>
                            <SelectRow
                                label={t("form.powerType")}
                                tooltip={t("connector.tooltip.powerType")}
                                value={chargingConnector?.powerType}
                                options={powerTypeOptions}
                                onChange={(v) => handleInputChange("powerType", v)}
                                invalid={invalidFields.includes("powerType")}
                                required
                            />

                            <InputRow
                                label={t("form.maxPowerKw")}
                                tooltip={t("connector.tooltip.maxElectricPower")}
                                value={chargingConnector?.maxElectricPower}
                                onChange={(v) => handleInputChange("maxElectricPower", v)}
                                isNumber
                            />

                            <InputRow
                                label={t("form.maxCurrentA")}
                                tooltip={t("connector.tooltip.maxAmperage")}
                                value={chargingConnector?.maxAmperage}
                                onChange={(v) => handleInputChange("maxAmperage", v)}
                                isNumber
                                invalid={invalidFields.includes("maxAmperage")}
                                required
                            />

                            <InputRow
                                label={t("form.maxVoltageV")}
                                tooltip={t("connector.tooltip.maxVoltage")}
                                value={chargingConnector?.maxVoltage}
                                onChange={(v) => handleInputChange("maxVoltage", v)}
                                isNumber
                                invalid={invalidFields.includes("maxVoltage")}
                                required
                            />
                        </FormGrid>
                    </FormSection>
                </FormGrid>
            </FormCard>
            <div
                className={`grid grid-cols-1 md:grid-cols-1 gap-8 mt-8 ${
                    invalidFields.includes("tariffIds") ? "ring-2 ring-error-500 rounded-md" : ""
                }`}
            >
                {!isEdit && !chargingConnector?.chargingPointPublicId ? (
                    <FormCard title={t("entity.tariffs")} description={t("connector.tooltip.tariffIds")}>
                        <p className="text-sm text-mute-400">
                            {t("connector.selectPointForTariffs")}
                        </p>
                    </FormCard>
                ) : (
                    <EntityMultiSelectForm
                        key={`${chargingConnector?.chargingPointPublicId || ""}-${tariffs.length}`}
                        title={t("entity.tariffs")}
                        description={t("connector.tooltip.tariffIds")}
                        fetchItems={async () => tariffs}
                        labelKey="tariffName"
                        valueKey="tariffPublicId"
                        selectedIds={selectedTariffIds}
                        onChange={(ids) => handleInputChange("tariffIds", ids)}
                        confirmRemove={!!isEdit}
                    />
                )}

                <div className="hidden md:block" />
            </div>

        </div>
    );
};

export default ConnectorForm;
